import { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Stack } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import moment from 'moment';
import TeleophthPatientConfirmationModal from 'src/products/teleophth/pages/TeleophthPatientConfirmationModal/TeleophthPatientConfirmationModal';
import { createOrderIDB } from 'src/services/offline/outboxClient';
import posthog, {
  POSTHOG_GROUP_TYPES,
  setPosthogGroups,
} from 'src/services/posthog';
import {
  captureExamEvent,
  EXAM_EVENTS,
  markExamStarted,
} from 'src/services/posthogEvents';
import {
  APP_EVENTS,
  captureAppEvent,
  reportCameraConnectionIssue,
} from 'src/services/posthogEvents';
import { v4 as uuidv4 } from 'uuid';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';
import WarnNoConnectedCameraModal from 'src/components/custom_components/WarnNoConnectedCameraModal';

import { ApplicationInstance } from 'src/models/Application.model';
import { DeviceDataView } from 'src/models/GalenData/DeviceDataView.model';
import { OrderData, OrderStatusEnum } from 'src/models/GalenData/Order.model';
import { GalenDataUser } from 'src/models/GalenData/User.model';
import { I18NextNS } from 'src/models/Language.model';
import { ReportTemplate } from 'src/models/ReportTemplate.model';
import { Session } from 'src/models/Session.model';

import useCheckCameraConnection from 'src/hooks/useCheckCameraConnection';
import useCreateRemoteOrder from 'src/hooks/useCreateRemoteOrder';
import useGetExistingSiteData from 'src/hooks/useGetExistingSiteData';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useNavigateAfterConfirm from 'src/hooks/useNavigateAfterConfirm';

import { getMatchedPatientWithOpenOrder } from 'src/utils/patientMatching';
import {
  validateNameLength,
  validateNamePattern,
  validatePID,
  validatePIDLength,
} from 'src/utils/validationRules';

import { SearchPatientFormValues } from '../SearchPatientInfoFormGroup/SearchPatientInfoFormGroup';
import MatchedFound from './MatchedFound';
import NewOrderCreationForm from './NewOrderCreationForm';

type NewPatientCreationProps = {
  isOpen: boolean;
  onClose: () => void;
  initialValues?: SearchPatientFormValues;
};

type FormValues = {
  dob: string;
  firstName: string;
  lastName: string;
  id: string;
  template: ReportTemplate;
  siteId: string;
  siteName: string;
};

export default function NewPatientCreation({
  isOpen,
  onClose,
  initialValues,
}: NewPatientCreationProps) {
  const { t } = useTranslation(I18NextNS.COMMON);

  const {
    practiceSettings: { product },
    updateSession,
  } = useRemoteBoundStore();

  const { getExistingSiteData } = useGetExistingSiteData();

  const { createPatient, createOrder } = useCreateRemoteOrder();

  const { showHttpRequestError } = useHandleHttpRequestError();
  const navigateAfterConfirm = useNavigateAfterConfirm();
  const { canStartExam, isCameraConnectionLost } = useCheckCameraConnection();

  const [matchedPatient, setMatchedPatient] = useState<GalenDataUser>();
  const [openOrder, setOpenOrder] = useState<DeviceDataView<OrderData>>();
  const [showCameraWarning, setShowCameraWarning] = useState(false);
  const [currentSession, setCurrentSession] = useState<Partial<Session>>();
  const [isShowResumeModal, setIsShowResumeModal] = useState(false);

  const handleCloseAndReset = () => {
    onClose();
    setMatchedPatient(undefined);
    setOpenOrder(undefined);
    setCurrentSession(undefined);
    setIsShowResumeModal(false);
    reset();
  };

  const getValidatedDefaultValue = (
    value: string | undefined,
    validator: (val: string) => boolean,
  ): string => {
    if (!value) return '';
    return validator(value) ? value : '';
  };

  const formMethods = useForm<FormValues>({
    defaultValues: {
      dob: '',
      firstName: '',
      lastName: '',
      id: '',
      template: ApplicationInstance(product).templates[0],
      siteId: '',
      siteName: '',
    },
  });

  const {
    handleSubmit,
    getValues,
    reset,
    setValue,
    formState: { isSubmitting },
  } = formMethods;

  useEffect(() => {
    getExistingSiteData();

    const dob = initialValues?.dob;
    const firstName = getValidatedDefaultValue(
      initialValues?.firstName,
      (val) => validateNamePattern(val) && validateNameLength(val),
    );
    const lastName = getValidatedDefaultValue(
      initialValues?.lastName,
      (val) => validateNamePattern(val) && validateNameLength(val),
    );
    const id = getValidatedDefaultValue(
      initialValues?.id,
      (val) => validatePID(val) && validatePIDLength(val),
    );

    if (dob) setValue('dob', dob);
    if (firstName) setValue('firstName', firstName);
    if (lastName) setValue('lastName', lastName);
    if (id) setValue('id', id);
  }, [getExistingSiteData, initialValues, setValue]);

  const handleSave = () =>
    handleSubmit(async (data) => {
      const orderCreated = await tryCreateOrder(data);
      if (orderCreated) reset();
    })();

  const handleProceedToExam = () => {
    if (!canStartExam) {
      reportCameraConnectionIssue(isCameraConnectionLost);
      setShowCameraWarning(true);
      return;
    }
    return handleSubmit(async (data) => {
      const orderCreated = await tryCreateOrder(data);
      if (orderCreated) navigateAfterConfirm();
    })();
  };

  const tryCreateOrder = useCallback(
    async (data: FormValues): Promise<boolean> => {
      try {
        const { firstName, lastName, dob, id, siteId, siteName } = data;

        const trimmedFirstName = firstName.trim();
        const trimmedLastName = lastName.trim();
        const trimmedId = id.trim();

        const formattedDob = moment(dob).format('YYYY-MM-DD');
        const age = moment().diff(dob, 'years');
        const { matchedPatient, openOrder } =
          await getMatchedPatientWithOpenOrder(data);

        if (matchedPatient && openOrder) {
          setMatchedPatient(matchedPatient);
          setOpenOrder(openOrder);
          return false;
        }

        const patientUserId = matchedPatient
          ? matchedPatient.userId
          : await createPatient({
              firstName: trimmedFirstName,
              lastName: trimmedLastName,
              dob: formattedDob,
              id: trimmedId,
              siteId,
            });

        const tempSession = {
          patientUserId,
          id: trimmedId,
          firstName: trimmedFirstName,
          lastName: trimmedLastName,
          dob: formattedDob,
          age,
          template: getValues('template'),
          siteId,
          siteName,
        };

        updateSession(tempSession);

        const orderId = uuidv4();
        const examId = uuidv4();
        await createOrderIDB({
          OrderId: orderId,
          OrderStatus: OrderStatusEnum.OPEN,
          ExamId: examId,
          SiteId: siteId,
        });
        await createOrder(
          orderId,
          patientUserId,
          tempSession.template,
          siteId,
          siteName,
        );
        updateSession({ iid: examId, orderId, graded: false });
        posthog.group(POSTHOG_GROUP_TYPES.ORDER, orderId);
        setPosthogGroups({ [POSTHOG_GROUP_TYPES.SITE]: siteId });
        markExamStarted();
        captureExamEvent(EXAM_EVENTS.EXAM_STARTED, { order_id: orderId });
        captureAppEvent(APP_EVENTS.ORDER_CREATED, { order_id: orderId });

        return true;
      } catch (error) {
        showHttpRequestError(error);
        return false;
      }
    },
    [
      createPatient,
      createOrder,
      getValues,
      updateSession,
      showHttpRequestError,
    ],
  );

  return (
    <>
      <CustomModal
        show={isOpen && !isShowResumeModal}
        onHide={handleCloseAndReset}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title data-test="new-order-modal-title">
            {openOrder
              ? t('patientFields.newOrderCreationMatchFound')
              : t('patientFields.newOrderCreation')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {matchedPatient && openOrder ? (
            <MatchedFound
              matchedPatient={matchedPatient}
              openOrder={openOrder}
              onGoBack={() => {
                setMatchedPatient(undefined);
                setOpenOrder(undefined);
              }}
              onResumedExam={(session) => {
                setCurrentSession(session);
                setIsShowResumeModal(true);
              }}
            />
          ) : (
            <FormProvider {...formMethods}>
              <NewOrderCreationForm onSubmit={tryCreateOrder} />
            </FormProvider>
          )}
        </Modal.Body>
        {!openOrder && (
          <Modal.Footer className="d-flex justify-content-between">
            <Button
              variant="primary-100"
              onClick={handleCloseAndReset}
              disabled={isSubmitting}
              data-test="new-order-cancel-button"
              data-track="exam.new-patient-modal.cancel"
              data-observe
            >
              {t('modal.cancel')}
            </Button>
            <Stack direction="horizontal" gap={2}>
              <Button
                variant="primary-100"
                onClick={handleSave}
                disabled={isSubmitting}
                data-test="new-order-save-create-button"
                data-track="exam.new-patient-modal.save-and-create-another"
                data-observe
              >
                {t('modal.saveAndCreatePatient')}
              </Button>

              <Button
                variant="primary"
                onClick={handleProceedToExam}
                disabled={isSubmitting}
                data-test="new-order-proceed-button"
                data-track="exam.new-patient-modal.proceed-to-exam"
                data-observe
              >
                {isSubmitting
                  ? t('modal.confirming')
                  : t('modal.proceedToExam')}
              </Button>
            </Stack>
          </Modal.Footer>
        )}

        <WarnNoConnectedCameraModal
          isVisible={showCameraWarning}
          onClose={() => setShowCameraWarning(false)}
        />
      </CustomModal>

      {currentSession && (
        <TeleophthPatientConfirmationModal
          formValues={currentSession}
          isShowConfirmModal={isShowResumeModal}
          handleCloseConfirmModal={() => {
            setIsShowResumeModal(false);
            handleCloseAndReset();
          }}
        />
      )}
    </>
  );
}
